// vavci-predictive.js — Buscador predictivo compartido de VAVCI.
// Muestra sugerencias de producto mientras se escribe, con la Predictive Search
// API oficial de Shopify (/search/suggest.json). Autocontenido: NO depende de los
// componentes internos de Horizon (robusto ante actualizaciones del tema).
//
// Lo usan la página 404 (vavci-404.liquid) y la de búsqueda (vavci-search.liquid).
// El prefijo de las clases CSS lo pone cada sección vía data-prefix, así que el
// panel hereda el estilo de su propia sección sin duplicar este archivo.
//
// Contrato de markup:
//   [data-vps-root]   raíz. Atributos:
//       data-prefix        prefijo de clase CSS (p. ej. "v404" o "vcol"); literal
//                          estático, solo [A-Za-z0-9_-]
//       data-suggest-url   routes.predictive_search_url
//       data-search-url    routes.search_url
//       data-currency      shop.currency        (formato de precio)
//       data-locale        request.locale.iso_code
//       data-no-results    texto si no hay sugerencias
//       data-view-all      texto del pie «Ver todos los resultados»
//     [data-vps-input]  el <input name="q"> (role=combobox)
//     [data-vps-panel]  el panel de resultados (role=listbox, hidden)

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;
const LIMIT = 6;

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

// Solo permitimos URLs relativas o http(s) (defensa en profundidad frente a esquemas raros).
// La barra inicial ha de ir sola: «//evil.example» (y «/\evil.example», que el navegador
// normaliza igual) es una URL con protocolo implícito y saldría del dominio.
function isSafeUrl(u) {
  return /^(https?:\/\/|\/(?![/\\]))/.test(String(u == null ? '' : u));
}

function init(root) {
  if (root.dataset.vpsBound === '1') return;
  root.dataset.vpsBound = '1';

  const input = root.querySelector('[data-vps-input]');
  const panel = root.querySelector('[data-vps-panel]');
  if (!input || !panel) return;

  // Se valida: acaba en un atributo class/id y en un selector CSS, así que un valor
  // con comillas o espacios inyectaría marcado o rompería querySelectorAll.
  const prefixOk = /^[A-Za-z0-9_-]+$/.test(root.dataset.prefix || '');
  if (!prefixOk) {
    // Avisamos, pero seguimos: un panel sin estilo es mejor que un panel muerto.
    // Sin el aviso, quien monte una tercera página no sabría por qué el panel sale sin CSS.
    console.warn(
      'vavci-predictive.js: data-prefix ausente o no válido en [data-vps-root] '
      + `(recibido: ${JSON.stringify(root.dataset.prefix)}). Debe ser [A-Za-z0-9_-]+, `
      + 'p. ej. data-prefix="vcol". Se usa "v404" y el panel saldrá sin los estilos de esta sección.',
      root
    );
  }
  const p = prefixOk ? root.dataset.prefix : 'v404';
  const base = root.dataset.suggestUrl || '/search/suggest';
  const currency = root.dataset.currency || 'EUR';
  const locale = root.dataset.locale || 'es';
  const noResults = root.dataset.noResults || 'Sin sugerencias';
  const searchUrl = root.dataset.searchUrl || '/search';
  const viewAll = root.dataset.viewAll || 'Ver todos los resultados';
  let money;
  try {
    money = new Intl.NumberFormat(locale, { style: 'currency', currency });
  } catch {
    money = new Intl.NumberFormat('es', { style: 'currency', currency: 'EUR' });
  }

  let timer = null;
  let controller = null;
  let items = [];
  let active = -1;
  let query = '';

  function fmtPrice(v) {
    const n = parseFloat(v);
    return Number.isFinite(n) ? money.format(n) : '';
  }

  function open() {
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function close() {
    if (controller) { controller.abort(); controller = null; }
    panel.hidden = true;
    panel.innerHTML = '';
    items = [];
    active = -1;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }

  function render() {
    active = -1;
    if (!items.length) {
      panel.innerHTML = `<p class="${p}__suggest-empty" role="presentation">${escapeHtml(noResults)}</p>`;
      open();
      return;
    }
    const list = items.map((prod, i) => {
      const rawImg = (prod.featured_image && prod.featured_image.url) || prod.image || '';
      const img = isSafeUrl(rawImg) ? rawImg : '';
      const imgHtml = img
        ? `<img class="${p}__suggest-img" src="${escapeHtml(img)}" alt="" loading="lazy" width="48" height="48">`
        : `<span class="${p}__suggest-img ${p}__suggest-img--ph" aria-hidden="true"></span>`;
      const brand = prod.vendor ? `<span class="${p}__suggest-brand">${escapeHtml(prod.vendor)}</span>` : '';
      const price = fmtPrice(prod.price);
      const priceHtml = price ? `<span class="${p}__suggest-price">${escapeHtml(price)}</span>` : '';
      const href = isSafeUrl(prod.url) ? prod.url : '#';
      return `<a class="${p}__suggest-item" role="option" aria-selected="false" id="${p}-opt-${i}" href="${escapeHtml(href)}">`
        + imgHtml
        + `<span class="${p}__suggest-info">${brand}<span class="${p}__suggest-title">${escapeHtml(prod.title)}</span></span>`
        + priceHtml
        + '</a>';
    }).join('');
    // options[prefix]=last: el predictivo busca por prefijo; sin esto, la búsqueda
    // completa (que por defecto va sin prefijo) no encuentra las palabras a medias
    // y el enlace acabaría en una página de «sin resultados».
    const allHref = `${searchUrl}?q=${encodeURIComponent(query)}&type=product&options[prefix]=last`;
    const footer = `<a class="${p}__suggest-all" href="${escapeHtml(allHref)}">${escapeHtml(viewAll)}</a>`;
    panel.innerHTML = list + footer;
    open();
  }

  function move(dir) {
    const opts = panel.querySelectorAll(`.${p}__suggest-item`);
    if (!opts.length) return;
    active = (active + dir + opts.length) % opts.length;
    opts.forEach((el, i) => {
      const on = i === active;
      el.classList.toggle('is-active', on);
      el.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    const el = opts[active];
    if (el) {
      el.scrollIntoView({ block: 'nearest' });
      input.setAttribute('aria-activedescendant', el.id);
    }
  }

  async function run(q) {
    if (controller) controller.abort();
    controller = new AbortController();
    const url = `${base}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=${LIMIT}`;
    try {
      const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      // Cerramos en vez de salir sin más: si no, un 429 o un 5xx a media escritura
      // dejaría a la vista los productos de la consulta anterior bajo un input ya cambiado.
      if (!res.ok) { close(); return; }
      const data = await res.json();
      if (input.value.trim() !== q) return; // el usuario ya cambió/limpió la consulta
      query = q;
      items = (data && data.resources && data.resources.results && data.resources.results.products) || [];
      render();
    } catch (err) {
      if (err.name !== 'AbortError') close();
    }
  }

  input.addEventListener('input', () => {
    const q = input.value.trim();
    clearTimeout(timer);
    if (q.length < MIN_CHARS) { close(); return; }
    timer = setTimeout(() => run(q), DEBOUNCE_MS);
  });

  input.addEventListener('keydown', (e) => {
    if (panel.hidden) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'Escape') { close(); }
    else if (e.key === 'Enter' && active >= 0 && items[active]) {
      e.preventDefault();
      // Se valida igual que el href, para que ninguna URL de la API llegue a una
      // navegación sin pasar por isSafeUrl.
      if (isSafeUrl(items[active].url)) window.location.href = items[active].url;
    }
  });

  document.addEventListener('click', (e) => {
    if (!root.contains(e.target)) close();
  });

  // Clic en una opción: no quitar el foco al input (así el click navega por el href).
  panel.addEventListener('mousedown', (e) => e.preventDefault());

  // Cierra si el foco sale del buscador (p. ej. al tabular fuera).
  root.addEventListener('focusout', () => {
    requestAnimationFrame(() => {
      if (!root.contains(document.activeElement)) close();
    });
  });
}

document.querySelectorAll('[data-vps-root]').forEach(init);

// Re-vincula al recargar la sección en el editor de temas (Shopify reemplaza el DOM).
document.addEventListener('shopify:section:load', (e) => {
  e.target.querySelectorAll('[data-vps-root]').forEach(init);
});
